/**
 * EntryListPublic.jsx
 * Read-only public entry list grouped by class.
 * Route: /entries/:calendarId
 */

import { useState, useEffect } from 'react';
import '../Secretary.css';

const API_KEY = import.meta.env.VITE_API_KEY || '';

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

export default function EntryListPublic() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const calendarId = window.location.pathname.split('/').filter(Boolean).pop();

  useEffect(() => {
    if (!calendarId || isNaN(calendarId)) {
      setError('No valid event ID in URL.');
      setLoading(false);
      return;
    }
    fetch(`/api/entry-list/${calendarId}`, { headers: { 'X-API-Key': API_KEY } })
      .then(async res => {
        if (res.status === 404) {
          window.location.replace('/404');
          return;
        }
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || 'HTTP ' + res.status);
        return json;
      })
      .then(setData)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [calendarId]);

  return (
    <div className="secretary-portal App">
      <header className="App-header">
        <h1>🏍️ NHCA Entry List</h1>
        <p>National Hillclimb Association</p>
      </header>

      <main className="secretary-main">
        {loading && (
          <div className="sec-loading"><div className="spinner" /><p>Loading entry list…</p></div>
        )}

        {error && (
          <div className="error-message"><h3>⚠️ Error</h3><p>{error}</p></div>
        )}

        {data && (
          <>
            <div className="sec-section" style={{ marginBottom: '1rem' }}>
              <div className="sec-section-header">
                <div>
                  <h2>🏁 {data.event}</h2>
                  <p style={{ color: 'var(--text-light)', margin: '0.25rem 0 0', fontSize: '1rem' }}>
                    {fmtDate(data.date)}
                  </p>
                </div>
                <span className="entry-count-badge">
                  {data.entry_count} entr{data.entry_count !== 1 ? 'ies' : 'y'}
                </span>
              </div>
            </div>

            {data.classes && data.classes.length === 0 && (
              <div className="sec-empty">
                <div className="sec-empty-icon">📋</div>
                <p>No entries yet for this event.</p>
              </div>
            )}

            {data.classes && data.classes.map(group => (
              <div key={group.class} className="sec-section" style={{ marginBottom: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <h3 style={{ color: 'var(--primary-color)', margin: 0, fontSize: '1.3rem' }}>
                    {group.class} Class
                  </h3>
                  <span className="entry-count-badge">{group.entries.length}</span>
                </div>
                <div className="entries-table-wrap">
                  <table className="entries-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Rider</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.entries.map(e => {
                        const rider = [e.rider_first, e.rider_last].filter(Boolean).join(' ') || '—';
                        const passenger = [e.passenger_first, e.passenger_last].filter(Boolean).join(' ');
                        const badgeStyle = (bg) => ({
                          display: 'inline-block', padding: '0.1rem 0.4rem',
                          borderRadius: '3px', fontSize: '0.72rem', fontWeight: 700,
                          color: 'white', background: bg, letterSpacing: '0.02em',
                        });
                        const badges = [
                          e.class_rl      && <span key="rl" style={badgeStyle('#1565c0')}>RL</span>,
                          e.class_vintage && <span key="v"  style={badgeStyle('#e65100')}>V</span>,
                          e.class_classic && <span key="c"  style={badgeStyle('#7b1fa2')}>C</span>,
                          e.class_fe      && <span key="fe" style={badgeStyle('#2e7d32')}>FE</span>,
                        ].filter(Boolean);
                        return (
                          <tr key={e.race_number}>
                            <td className="race-num-cell">{e.race_number}</td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                <span>{rider}</span>
                                {badges}
                              </div>
                              {passenger && <div style={{ color: 'var(--text-light)', fontSize: '0.9em', marginTop: '0.2rem' }}>{passenger}</div>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}

            {data.invalid && data.invalid.length > 0 && (
              <div className="sec-section" style={{ borderLeft: '4px solid var(--warning-color)' }}>
                <h3 style={{ color: 'var(--warning-color)', marginBottom: '0.75rem' }}>
                  ⚠️ Incomplete Entries ({data.invalid.length})
                </h3>
                <p style={{ color: 'var(--text-light)', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                  These entries are missing CC information and have not been assigned a class.
                </p>
                <div className="entries-table-wrap">
                  <table className="entries-table">
                    <thead>
                      <tr><th>#</th><th>Rider</th></tr>
                    </thead>
                    <tbody>
                      {data.invalid.map(e => (
                        <tr key={e.race_number}>
                          <td className="race-num-cell">{e.race_number}</td>
                          <td>{[e.rider_first, e.rider_last].filter(Boolean).join(' ') || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <p style={{ color: 'var(--text-light)', fontSize: '0.8rem', marginTop: '1rem', textAlign: 'right' }}>
              Retrieved {new Date(data.retrieved_at).toLocaleString('en-GB')}
            </p>
          </>
        )}
      </main>

      <footer className="App-footer">
        <p>NHCA Entry List | Read-only view</p>
      </footer>
    </div>
  );
}
